import React, { useEffect, useState } from 'react';
import { Icon } from '../Svg/icon';
import {
  InformationIcon,
  StyledAttributeDescription,
  StyledAttributeValue,
  StyledH3,
  StyledOptionContainer,
  StyledRadioGroup,
  SwatchLabelContainer,
  SwatchTitleContainer
} from './styled';
import { Modal, ModalType } from '../../../../src/store/modal/constants';
import { scrollToParent } from './helpers';
import { SwatchSelectProps } from './types';
import { WeddingLayflatLink } from '../../../../src/features/pdp/components/WeddingLayflatLink';
import { OPTION_THEME, SKU_LAYFLAT } from '../../../../src/components/constants/products';
import { useProduct } from '../../../../src/features/pdp';
import cx from 'classix';

export const scrollIntoSelectedSwatch = (selectedSwatchId: string, currentModal: Modal): void => {
  if (selectedSwatchId) {
    const currentModalType = currentModal.type;
    try {
      const inputContainerElements = document.querySelectorAll(`[id=${CSS.escape(selectedSwatchId)}]`);
      inputContainerElements.forEach(element => {
        if (currentModalType === ModalType.LIVE_PREVIEW_MODAL || currentModalType === ModalType.PRODUCT_OPTIONS_MODAL) {
          //If we are in the modal, and we change the selected swatch, scroll to the element on the pdp
          scrollToParent(element, '.PdpApp');
        }
      });
    } catch (error) {
      console.error('Error scrolling into selected swatch: ', error);
    }
  }
};

const SwatchSelect = ({
  id,
  label,
  displayName,
  hoverLabel,
  className,
  children,
  overflowWrap,
  gridLayout,
  hideLabels,
  onTitleClick,
  onDoubleClick,
  onDoubleTap,
  selectedFieldId,
  currentModal,
  description
}: SwatchSelectProps): JSX.Element => {
  const { product: { sku } } = useProduct();

  const [tapCount, setTapCount] = useState(0);
  const handleTouchStart = () => {
    if (tapCount === 1) {
      setTapCount(0);
      if (onDoubleTap) {
        onDoubleTap();
      }
    } else {
      setTapCount(tapCount + 1);
      setTimeout(() => setTapCount(0), 300);
    }
  };

  const [isMouseHover, setIsMouseHover] = useState(false);

  useEffect(() => {
    const isMobile = typeof window === 'object' && window.innerWidth < 768;
    isMobile && scrollIntoSelectedSwatch(`option-value-label-${selectedFieldId}`, currentModal);
  }, [currentModal, selectedFieldId]);

  const isLayflatPdp = sku === SKU_LAYFLAT;
  const isThemeOption = label.toLowerCase() === OPTION_THEME;

  const shouldShowWeddingLayflatLink = isLayflatPdp && isThemeOption;

  return (
    <StyledOptionContainer
      id={`option-${id}`}
      className={className}
      onDoubleClick={onDoubleClick}
      onTouchStart={handleTouchStart}
      onMouseOver={() => setIsMouseHover(true)}
      onMouseOut={() => setIsMouseHover(false)}
    >
      {!hideLabels && (
        <SwatchLabelContainer>
          <SwatchTitleContainer>
            <StyledH3 id={`option-title-${id}`} aria-label={label} onClick={onTitleClick}>
              {displayName.toLowerCase()}
            </StyledH3>
            <StyledAttributeValue id={`${label}-selected-option-value`} onClick={onTitleClick}>
              {hoverLabel}
            </StyledAttributeValue>
            {onTitleClick && (
              <InformationIcon visible={isMouseHover}>
                <Icon icon="info" />
              </InformationIcon>
            )}
          </SwatchTitleContainer>
          {description && (
            <StyledAttributeDescription>{description}</StyledAttributeDescription>)
          }
        </SwatchLabelContainer>
      )}
      <StyledRadioGroup options={children.length} role="radiogroup" className={cx(gridLayout && 'grid')}
                        overflowWrap={overflowWrap} id={id}>
        {children}
      </StyledRadioGroup>

      {shouldShowWeddingLayflatLink && <WeddingLayflatLink />}
    </StyledOptionContainer>
  );
};

export default SwatchSelect;
